import Chart from 'react-apexcharts';
import { ReactElement, useState, useMemo, useEffect } from 'react';
import SpeedFilter from './SpeedFilter';
import Histogram from './Histogram';
import SegmentModeSelector, { SegmentMode } from './SegmentModeSelector';
import { getChannel } from '../../../utils/channelNamesGridded';

function HistogramWaist(props: { filterResults: any }): ReactElement {
  const filteredLengths = props.filterResults?.filteredLengths || {};
  const speedFilteredLength = filteredLengths['RoadSpeed'] || 0;
  const waistFilteredLength = filteredLengths['BodyPosWaistDelta_sG21_cm'] || 0;

  const belowWaist = speedFilteredLength
    ? Math.round(((speedFilteredLength - waistFilteredLength) / speedFilteredLength) * 100)
    : 0;
  const belowWaistData = [belowWaist];

  const filterValues = props.filterResults?.filterValues || {};
  const waistAmount = `${filterValues['BodyPosWaistDelta_sG21_cm'] || -150}mm`;
  const belowWaistLabel = [`Waist < ${waistAmount}`];

  const options = {
    noData: { text: 'There is no data to display' },
    chart: {
      id: 'basic-bar',
    },
    xaxis: {
      categories: belowWaistLabel,
      position: 'bottom',
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
    },
    yaxis: {
      opposite: true,
      min: 0,
      max: 100,
    },
    tooltip: {
      enabled: true,
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        dataLabels: {
          position: 'top', // top, center, bottom
        },
      },
    },
    colors: ['rgba(254, 176, 25, 0.85)'],
    dataLabels: {
      enabled: true,
      formatter: (val: any): string => (val ? val + '%' : ''),
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    title: {
      text: 'Standing',
      floating: false,
      offsetY: 0,
      style: {
        align: 'center',
        color: '#444',
        fontSize: '20px',
      },
    },
  };

  const series = [
    {
      name: 'Percentage at Waist Reference position',
      data: belowWaistData,
    },
  ];

  return <Chart options={options} series={series} type="bar" width="100%" height="380" />;
}

function BodyHistogram(props: { run: { data_gridded: any }; calibration: any }): ReactElement {
  const data_gridded = props.run.data_gridded || {};
  const calibration = props.calibration;

  if (
    !getChannel(data_gridded, 'BodyPosChestDelta_sG21_cm', calibration) &&
    !getChannel(data_gridded, 'BodyPosHeadDelta_sG21_cm', calibration)
  ) {
    return <div>This run has no head or chest position data</div>;
  }

  const [data, setData] = useState();
  const [filterResults, setFilterResults] = useState();

  // Test-segment scoping: Gate_NG10 is the per-sample active-traverse counter
  // (0 outside active laps/traverses, N inside active segment N).
  const gateChannel: number[] = data_gridded.Gate_NG10 || [];
  const segmentAvailable = useMemo(() => gateChannel.some((value: number) => value > 0), [data_gridded]);
  const [segmentMode, setSegmentMode] = useState<SegmentMode>(segmentAvailable ? 'segment' : 'all');
  useEffect(() => {
    if (!segmentAvailable && segmentMode === 'segment') setSegmentMode('all');
  }, [segmentAvailable, segmentMode]);
  const sampleMask = useMemo(
    () => (segmentMode === 'segment' && segmentAvailable ? gateChannel.map((value: number) => value > 0) : null),
    [segmentMode, segmentAvailable, data_gridded],
  );

  const filterFields = useMemo(
    () => [
      {
        fieldName: 'RoadSpeed',
        displayName: 'Speed',
        min: 0,
        max: 50,
        step: 1,
        unit: 'km/h',
        conversionFactor: 1,
        defaultValue: 30,
      },
      {
        fieldName: 'BodyPosWaistDelta_sG21_cm',
        displayName: 'Waist',
        min: -250,
        max: 100,
        step: 10,
        unit: 'mm',
        conversionFactor: 10.0, // Convert to mm
        defaultValue: -150,
      },
    ],
    [],
  );

  const dataFields = useMemo(
    () => [
      {
        fieldName: 'BodyPosChestDelta_sG21_cm',
        displayName: 'Chest',
      },
      {
        fieldName: 'BodyPosHeadDelta_sG21_cm',
        displayName: 'Head',
      },
    ],
    [],
  );

  return (
    <div className="cr-mb-5 cr-overflow-hidden cr-rounded-xl cr-bg-clip-border cr-p-5 cr-shadow-md">
      {data && (
        <div className="cr-flex cr-flex-row">
          <div className="cr-basis-11/12">
            <Histogram
              data={data}
              title="FORMA Time in Position"
              series={[
                { fieldName: 'BodyPosChestDelta_sG21_cm', displayName: 'Percentage at Chest Reference position' },
                { fieldName: 'BodyPosHeadDelta_sG21_cm', displayName: 'Percentage at Head Reference position' },
              ]}
              thresholds={() => Array.from({ length: 15 }, (_, i) => i - 6)} // [-4,-3,-2,-1,0,1,2,3,4,5,6,7,8,9,10]
              unit="cm"
            />
          </div>
          <div className="cr-basis-1/12">
            <HistogramWaist filterResults={filterResults}></HistogramWaist>
          </div>
        </div>
      )}
      <SpeedFilter
        rawData={data_gridded}
        setData={setData}
        setFilterResults={setFilterResults}
        calibration={calibration}
        filterFields={filterFields}
        dataFields={dataFields}
        sampleMask={sampleMask}
        extraControls={
          <SegmentModeSelector
            name="body-histogram-segment-mode"
            mode={segmentMode}
            setMode={setSegmentMode}
            segmentAvailable={segmentAvailable}
          />
        }
      />
    </div>
  );
}

export default BodyHistogram;
